import React, { useMemo, useCallback } from "react";
import "./IdentityDistributionCard.css";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import variablePieModule from "highcharts/modules/variable-pie";
import { useTheme } from "../../../../context/ThemeContext";

if (typeof variablePieModule === "function") {
  variablePieModule(Highcharts);
}

/* 
  Professional palette with carefully curated colors that work 
  harmoniously together — no clashing or overly saturated tones.
*/
const GOLDEN_RATIO_CONJUGATE = 0.618033988749895;

const generateDistinctColor = (index, totalCount) => {
  let hueNormalized = (0.17 + index * GOLDEN_RATIO_CONJUGATE) % 1.0;
  let hue = Math.round(hueNormalized * 360);

  if (hue >= 50 && hue <= 95) {
    hue = (hue + 25) % 360;
  }


  let saturation, lightness;

  if (totalCount <= 8) {
    saturation = 75;
    lightness = 56;
  } else if (totalCount <= 16) {
    saturation = 68;
    lightness = 58;
  } else {
    saturation = 60;
    lightness = 62;
  }

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

const buildColorPalette = (count) => {
  return Array.from({ length: count }, (_, i) =>
    generateDistinctColor(i, count)
  );
};


const IdentityDistributionCard = ({ apiResponse }) => {
  const rawData = apiResponse?.data || {};
  const dataEntries = Object.entries(rawData);
  const totalIdentities = Object.values(rawData).reduce(
    (sum, val) => sum + val,
    0
  );

  const { isDark } = useTheme();

  const colorPalette = useMemo(
    () => buildColorPalette(dataEntries.length),
    [dataEntries.length]
  );

  const chartSeriesData = useMemo(() => {
    return dataEntries.map(([key, value], index) => {
      const structuralWeight = value > 0 && value < 15 ? 15 : value;
      return {
        name: key,
        y: structuralWeight,
        z: structuralWeight,
        actualCount: value,
        color: colorPalette[index],
      };
    });
  }, [apiResponse]);

  const renderCenterText = useCallback(
    function () {
      const chart = this;
      const series = chart.series && chart.series[0];

      if (!series || !series.center) return;

      // Exact center of the donut hole in absolute chart coordinates
      const cx = chart.plotLeft + series.center[0];
      const cy = chart.plotTop + series.center[1];

      const formattedTotal = totalIdentities.toLocaleString();

      // Destroy previous elements to prevent stacking on redraws
      if (chart.customTotalValue) {
        chart.customTotalValue.destroy();
        chart.customTotalValue = null;
      }
      if (chart.customTotalLabel) {
        chart.customTotalLabel.destroy();
        chart.customTotalLabel = null;
      }

      // Main number — rendered as native SVG text for pixel-perfect centering
      chart.customTotalValue = chart.renderer
        .text(formattedTotal, 0, 0)
        .css({
          fontSize: "16px",
          fontWeight: "700",
          color: "#0cb599",
          textAlign: "center",
          fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
        })
        .attr({
          zIndex: 10,
        })
        .add();

      // Measure rendered bounding box, then reposition to exact center
      const valueBBox = chart.customTotalValue.getBBox();
      chart.customTotalValue.attr({
        x: cx - valueBBox.width / 2,
        y: cy + valueBBox.height / 4, // baseline adjustment for vertical centering
      });

      // "Total" label below the number
      chart.customTotalLabel = chart.renderer
        .text("TOTAL", 0, 0)
        .css({
          fontSize: "10px",
          fontWeight: "400",
          color: "#9ca3af",
          textAlign: "center",
          letterSpacing: "0.5px",
          fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
        })
        .attr({
          zIndex: 10,
        })
        .add();

      const labelBBox = chart.customTotalLabel.getBBox();
      chart.customTotalLabel.attr({
        x: cx - labelBBox.width / 2,
        y: cy + valueBBox.height / 4 + labelBBox.height + 4,
      });
    },
    [totalIdentities]
  );

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "variablepie",
        backgroundColor: "transparent",
        height: 260,
        spacing: [10, 10, 10, 10],
        style: {
          fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
        },
        events: {
          render: renderCenterText,
        },
      },
      title: { text: null },
      credits: { enabled: false },
      tooltip: {
        backgroundColor: isDark ? "#1b3157" : "#ffffff",
        borderColor: isDark ? "#233554" : "#E5E7EB",
        borderRadius: 8,
        shadow: {
          color: "rgba(0,0,0,0.06)",
          offsetX: 0,
          offsetY: 2,
          width: 6,
        },
        style: {
          fontSize: "12px",
          color: isDark ? "#E6F1FF" : "#374151",
          fontWeight: "500",
        },
        headerFormat: "",
        pointFormatter: function () {
          const pct =
            totalIdentities > 0
              ? ((this.actualCount / totalIdentities) * 100).toFixed(1)
              : 0;
          return (
            `<span style="color:${this.color}">\u25CF</span> <b>${this.name}</b><br/>` +
            `Count: <b>${this.actualCount.toLocaleString()}</b><br/>` +
            `Share: <b>${pct}%</b>`
          );
        },
        useHTML: true,
      },
      plotOptions: {
        variablepie: {
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: { enabled: false },
          showInLegend: false,
          innerSize: "45%",
          borderWidth: 2,
          borderColor: "#ffffff",
          states: {
            hover: {
              brightness: 0.05,
              halo: {
                size: 6,
                opacity: 0.2,
              },
            },
            inactive: {
              opacity: 0.5,
            },
          },
        },
      },
      series: [
        {
          minPointSize: "30%",
          maxPointSize: "100%",
          zMin: 0,
          data: chartSeriesData,
          animation: {
            duration: 800,
            easing: "easeOutQuart",
          },
        },
      ],
    }),
    [chartSeriesData, renderCenterText, totalIdentities, isDark]
  );

  return (
    <div className="analytics-metric-card">
      <div className="card-header-group">
        <h3 className="card-main-title">
          Identity &amp; Application Distribution
        </h3>
        <p className="card-subtitle-desc">
          Breakdown of all identity types and applications
        </p>
      </div>

      <div className="chart-wrapper-relative">
        <div className="highcharts-responsive-container">
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </div>

        <div className="identity-legend-grid">
          {chartSeriesData.map((item) => (
            <div key={item.name} className="legend-flex-item">
              <div className="legend-label-row">
                <div
                  className="legend-dot-indicator"
                  style={{ backgroundColor: item.color }}
                />
                <p className="legend-text-label" title={item.name}>
                  {item.name}
                </p>
              </div>
              <p className="legend-value-bold">
                {item.actualCount.toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IdentityDistributionCard;